import { Button, Upload, UploadProps } from "antd";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import CloseIcon from "../../assets/icons/CloseIcon";
import { useUploadFileMutation } from "../../features/auth/authApi";
import useError from "../../hooks/useError";
import { toast } from "../../lib/toast";
import { stringToArray } from "../../utils/array";
import { getExtension } from "../../utils/string";

type Props = {
  errorMessage?: string;
  form: any;
  name: string;
  disabled?: boolean;
  fileType?: boolean;
  accept?: string;
  onChange?: (url: string | null) => void;
  listFile?: boolean;
  size?: "large" | "middle" | "small";
  fileSize?: number;
  component?: (props: {
    loading: boolean;
    file?: string;
    closeIcon?: React.ReactNode;
  }) => JSX.Element;

  hideClearIcon?: boolean;
} & Omit<UploadProps, "name">;

function accessFile(accept: string) {
  return accept?.toLowerCase().includes("png") ||
    accept?.toLowerCase().includes("jpg") ||
    accept?.toLowerCase().includes("jpeg") ||
    accept?.toLowerCase().includes("svg") ||
    accept?.toLowerCase().includes("gif")
    ? true
    : false;
}

export default function FileUploader({
  form,
  name,
  disabled,
  onChange,
  accept = ".png,.jpeg",
  listFile = true,
  fileType = true,
  size = "large",
  fileSize,
  component: Component,
  hideClearIcon,
  errorMessage = "File upload fail",
}: Props) {
  const [fileName, setFileName] = useState("No file chosen");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadFile, { isLoading, isError, error, data, isSuccess }] =
    useUploadFileMutation();
  useError(isError, error, errorMessage);

  const handleUpload = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    const isImage = accessFile(getExtension(file?.name));

    const currentFileSize = fileSize || (isImage ? 5 : 10);

    const MAX_FILE_SIZE = currentFileSize * 1024 * 1024; // MB in bytes

    if (file?.size > MAX_FILE_SIZE) {
      toast(
        "error",
        `The file size exceeds the ${currentFileSize}MB limit. Please upload a smaller file`
      );
      return false;
    }

    uploadFile(formData);
    return false;
  };

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setFileList(info.fileList);

    if (info.file.status === "removed") {
      onChange && onChange(null);
      setFileName("No file chosen");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setFileName(data?.Location);
      onChange && onChange(data?.Location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (fileType) {
      const timer = setTimeout(() => {
        const file = form.getFieldValue(
          true ? stringToArray(name, ".") : [name]
        );

        if (file) {
          onChange && onChange(file);
          setFileName(file);
        }
      }, 1000);

      const file = form.getFieldValue(
        listFile ? stringToArray(name, ".") : [name]
      );

      if (file) {
        onChange && onChange(file);
        setFileName(file);
      }

      return () => {
        clearTimeout(timer);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, name, fileType, listFile]);

  const fileNameField = fileName?.split("/")?.pop();

  return (
    <div className="relative">
      <Upload
        name="file"
        beforeUpload={handleUpload}
        maxCount={1}
        accept={accept}
        showUploadList={false}
        onChange={handleChange}
        fileList={fileList}
        onDrop={(e) => {
          e.preventDefault();
          const droppedFiles = e.dataTransfer.files;
          if (droppedFiles.length > 0) {
            const file = droppedFiles[0];
            handleUpload(file);
            onChange && onChange(file.name);
          }
        }}
        disabled={disabled || isLoading}
      >
        {Component ? (
          <Component
            loading={isLoading}
            file={
              fileNameField && fileNameField !== "No file chosen"
                ? fileName
                : undefined
            }
            closeIcon={
              <span
                className="text-body-text cursor-pointer size-4 flex items-center justify-center w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange && onChange(null);
                  setFileName("");
                }}
              >
                <CloseIcon width={8} height={8} />
              </span>
            }
          />
        ) : (
          <Button
            className={`w-full flex items-center !justify-start !p-0 !border-display-medium rounded-md relative ${
              size === "large" ? "!h-[46px]" : ""
            }`}
            block
            size={size}
            disabled={disabled}
            htmlType="button"
          >
            <span className="!flex justify-center items-center px-3 bg-[#5e718d] text-white h-full rounded-s">
              Browse file
            </span>
            <div className="px-2.5 gap-2 text-sm font-medium text-extra-gray overflow-hidden whitespace-nowrap text-ellipsis">
              {isLoading ? (
                "Uploading..."
              ) : (
                <span
                  className="overflow-hidden text-ellipsis"
                  style={{
                    direction: "unset",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(fileNameField && fileNameField?.length > 30
                    ? `${fileNameField?.slice(0, 30)}...`
                    : fileNameField) || "No file chosen"}
                </span>
              )}
            </div>
          </Button>
        )}
      </Upload>
      {!hideClearIcon &&
      fileNameField &&
      fileNameField !== "No file chosen" &&
      !Component ? (
        <span
          className="absolute right-3 top-2/4 -translate-y-2/4 text-body-text cursor-pointer size-4 flex items-center justify-center"
          onClick={() => {
            onChange && onChange(null);
            setFileName("");
          }}
        >
          <CloseIcon width={8} height={8} />
        </span>
      ) : null}
    </div>
  );
}
